import { AwardTypeCode, type StockAdminQueue, type TableSort } from '@myrewards/contracts';
import { STAGE_WORDS } from '../../awards/present';
import {
  daysBetween,
  filterBarFilters,
  filterRows,
  fiscalYearOn,
  organizationDate,
  type ColumnSort,
  type FilterBarFilter,
  type FilterOption,
  type ListFilter,
} from '../../ui';
import { STEP, TYPE_SHORT } from '../labels';
import { ageDays, claimApplies, STEPS } from './presenter';

// Sorting and filtering the Stock Admin queue (APP-149 D3, D4): the Home queue card and the Queue
// page's views share these, so a column sorts and a filter counts the same on both. The page sorts
// and filters the whole list, then pages it; nothing here widens what the reader was sent.

type Row = StockAdminQueue['rows'][number];

/** The order the queue opens in (D3): Submitted, the oldest first. Ties go by award number. */
export const QUEUE_DEFAULT_SORT: TableSort = { column: 'submitted', dir: 'asc' };

/** Ties go by award number (D3), whichever way the column runs. */
export const byAwardNumber = (row: Row): string => row.award.displayId;

/** "oldest first", as "Back to oldest first" says the queue's own order. */
export const QUEUE_DEFAULT_WORDS = 'oldest first';

/**
 * APP-244 (Spot redesign): the Queue page opens with my work first: what I've claimed, then what's
 * waiting for a claim, then the rest, each the oldest submitted first. Every filter stays. The
 * Home card keeps oldest first.
 */
export const QUEUE_MY_WORK_FIRST: TableSort = { column: 'claim', dir: 'asc' };
export const QUEUE_MY_WORK_WORDS = 'my work first';

/** Who holds a row, for the Claim filter and its sort. Every row is exactly one of these. */
export type ClaimState = 'mine' | 'others' | 'waiting' | 'none';

export function claimState(row: Row, meId: string): ClaimState {
  if (row.claimedBy !== null) return row.claimedBy.id === meId ? 'mine' : 'others';
  return claimApplies(row) ? 'waiting' : 'none';
}

// My work first (APP-244): mine, then waiting for a claim, then the team's, then no claim here.
const CLAIM_RANK: Record<ClaimState, number> = { mine: 0, waiting: 1, others: 2, none: 3 };

/** A submitted instant as a sortable word, so a claim group runs oldest first. */
const submittedKey = (row: Row): string => String(Date.parse(row.submittedAt)).padStart(16, '0');

/** A row's stage, as the Stage column says it: the team's step while it has one, else the stage. */
export function stageKey(row: Row): string {
  return row.step ?? row.stage ?? 'draft';
}

/** Stages in the order an award moves through them; the Stage column sorts by it. */
const STAGE_ORDER: readonly string[] = [
  'draft',
  'approval',
  ...STEPS,
  'with_stock_admin',
  'paid',
  'closed',
];

function stageRank(row: Row): number {
  const at = STAGE_ORDER.indexOf(stageKey(row));
  return at === -1 ? STAGE_ORDER.length : at;
}

/** A stage key's words: the step's ("Checking"), else the plain stage's ("Approval"). */
function stageLabel(key: string): string {
  if ((STEPS as readonly string[]).includes(key)) return STEP[key as keyof typeof STEP].label;
  return STAGE_WORDS[key as keyof typeof STAGE_WORDS].label;
}

/** The submitted day, in the organization's calendar. */
const submittedDay = (row: Row): string => organizationDate(row.submittedAt);

/** The queue's columns that sort, by id (the Home card has every one but Age). */
export type QueueColumnId =
  | 'award'
  | 'submitted'
  | 'stage'
  | 'claim'
  | 'type'
  | 'pool'
  | 'people'
  | 'cash'
  | 'options'
  | 'age';

/**
 * How each queue column sorts, by column id. Dates compare as instants (an offset can differ),
 * the stage by the order awards move through it, and the claim by who holds it (my work first,
 * APP-244): you, then waiting for a claim, then the rest of the team by name, then no claim at
 * this step, each the oldest submitted first.
 */
export function queueSorts(meId: string, asOf: string): Record<QueueColumnId, ColumnSort<Row>> {
  return {
    award: { value: (row) => row.award.displayId },
    submitted: {
      value: (row) => Date.parse(row.submittedAt),
      words: ['oldest first', 'newest first'],
    },
    stage: { value: stageRank, words: ['earliest stage first', 'latest stage first'] },
    claim: {
      value: (row) => {
        const state = claimState(row, meId);
        return state === 'others'
          ? `${String(CLAIM_RANK.others)} ${row.claimedBy?.name ?? ''} ${submittedKey(row)}`
          : `${String(CLAIM_RANK[state])} ${submittedKey(row)}`;
      },
      words: ['my work first', 'my work last'],
    },
    type: { value: (row) => TYPE_SHORT[row.type] },
    pool: { value: (row) => row.pool },
    people: { value: (row) => row.people },
    cash: { value: (row) => row.cashCents },
    // Cash-only awards have no options: they go last either way.
    options: { value: (row) => row.options },
    // How long it has been with the team: the fewest days first, then the most.
    age: {
      value: (row) => ageDays(row.since, asOf),
      words: ['newest first', 'oldest first'],
    },
  };
}

/** The queue's filters (D4), by their id in the address. */
export type QueueFilterId = 'claim' | 'stage' | 'type' | 'pool' | 'submitted';

const CLAIM_OPTIONS: readonly FilterOption[] = [
  { value: 'anyone', label: 'Anyone' },
  { value: 'mine', label: 'Claimed by me' },
  { value: 'others', label: 'Claimed by others' },
  { value: 'waiting', label: 'Waiting for a claim' },
  { value: 'none', label: 'No claim at this step' },
];

const SUBMITTED_OPTIONS: readonly FilterOption[] = [
  { value: 'any', label: 'Any time' },
  { value: 'week', label: 'Last 7 days' },
  { value: 'month', label: 'Last 30 days' },
  { value: 'year', label: 'This fiscal year' },
];

const ALL = 'all';

/** Whether a row was submitted within the period: the last 7 or 30 days count today as one. */
function submittedWithin(row: Row, period: string, asOf: string): boolean {
  if (period === 'week' || period === 'month') {
    const days = daysBetween(submittedDay(row), asOf);
    return days < (period === 'week' ? 7 : 30);
  }
  if (period === 'year') return fiscalYearOn(submittedDay(row)) === fiscalYearOn(asOf);
  return true;
}

/**
 * Every param a queue table keeps in the address: its sort and its filters. The screen sorts and
 * filters what it holds, so a change to these alone never needs the route's loaders again.
 */
export const QUEUE_TABLE_PARAMS: readonly string[] = [
  'sort',
  'dir',
  'claim',
  'stage',
  'type',
  'pool',
  'submitted',
];

/** Every filter the queue has, in the bar's order. */
export const QUEUE_FILTER_ORDER: readonly QueueFilterId[] = [
  'claim',
  'stage',
  'type',
  'pool',
  'submitted',
];

/**
 * The queue's filters (D4), the ones a view names, in the bar's order. The claim and the period
 * list every choice; stages, types and pools list what the list holds. Every value is on a
 * strict list: one the address names that isn't there reads as the first choice, never echoed.
 */
export function queueFilterDefs(
  rows: readonly Row[],
  ids: readonly QueueFilterId[],
  ctx: { meId: string; asOf: string },
): Partial<Record<QueueFilterId, ListFilter<Row>>> {
  const pools = [...new Set(rows.map((row) => row.pool))].sort((a, b) => a.localeCompare(b));
  const all: Record<QueueFilterId, ListFilter<Row>> = {
    claim: {
      name: 'Claim',
      options: CLAIM_OPTIONS,
      passes: (row, value) => claimState(row, ctx.meId) === value,
      listAll: true,
    },
    stage: {
      name: 'Stage',
      options: [
        { value: ALL, label: 'All stages' },
        ...STAGE_ORDER.map((key) => ({ value: key, label: stageLabel(key) })),
      ],
      passes: (row, value) => stageKey(row) === value,
    },
    type: {
      name: 'Type',
      options: [
        { value: ALL, label: 'All types' },
        ...AwardTypeCode.options.map((code) => ({ value: code, label: TYPE_SHORT[code] })),
      ],
      passes: (row, value) => row.type === value,
    },
    pool: {
      name: 'Sector / pool',
      options: [
        { value: ALL, label: 'All' },
        ...pools.map((pool) => ({ value: pool, label: pool })),
      ],
      passes: (row, value) => row.pool === value,
    },
    submitted: {
      name: 'Submitted',
      options: SUBMITTED_OPTIONS,
      passes: (row, value) => submittedWithin(row, value, ctx.asOf),
      listAll: true,
    },
  };
  return Object.fromEntries(ids.map((id) => [id, all[id]]));
}

/** The rows every set filter lets through. */
export function filterQueueRows(
  rows: readonly Row[],
  values: Partial<Record<QueueFilterId, string>>,
  ctx: { meId: string; asOf: string },
): Row[] {
  return filterRows(rows, queueFilterDefs(rows, QUEUE_FILTER_ORDER, ctx), values);
}

/**
 * The bar's filters with their counts (D4): each choice counts what it would show with the other
 * filters as they are.
 */
export function queueFilterBar(
  rows: readonly Row[],
  values: Partial<Record<QueueFilterId, string>>,
  ids: readonly QueueFilterId[],
  ctx: { meId: string; asOf: string },
): FilterBarFilter<QueueFilterId>[] {
  return filterBarFilters(rows, queueFilterDefs(rows, ids, ctx), values);
}
